/**
 * Suivi de réparation public — utilise l'endpoint RGPD /api/public/suivi/{ref}
 * (aucune donnée personnelle : modèle, statut, dates, initiales).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, CheckCircle, Wrench, ScanLine } from 'lucide-react';
import axios from 'axios';
import QrScannerModal from '../public/QrScannerModal';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
export const REF_RE = /^REM\d{2,8}$/i;

export function normalizeRef(v) {
  return String(v || '').toUpperCase().replace(/\s+/g, '');
}

export async function fetchSuivi(ref) {
  const r = await axios.get(`${API}/api/public/suivi/${encodeURIComponent(normalizeRef(ref))}`);
  return r.data;
}

function statusClass(key) {
  if (['termine', 'a_recuperer', 'livre'].includes(key)) return 'done';
  if (key === 'en_cours') return 'now';
  return 'wait';
}

export function TrackResult({ data, compact = false }) {
  const { t, formatDate } = useLanguage();
  if (!data) return null;
  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return formatDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const cls = statusClass(data.status_key);
  return (
    <div className="res" data-testid="remobiles-track-result">
      <div className="head">
        <div><small>{t('track.label_ref')}</small><b className="mono">{data.ref}</b></div>
        <span className={`stat ${cls}`}>{cls === 'done' ? <CheckCircle /> : <Wrench />}{data.status_label || t('track.status_pending')}</span>
      </div>
      <div className="rows">
        {data.model && <div><span>{t('track.label_device')}</span><span>{data.model}</span></div>}
        {data.received_at && <div><span>{t('track.label_received')}</span><span>{fmtDate(data.received_at)}</span></div>}
        {data.expected_at && !data.completed_at && <div><span>{t('track.label_expected')}</span><span>{fmtDate(data.expected_at)}</span></div>}
        {data.completed_at && <div><span>{t('track.label_completed')}</span><span>{fmtDate(data.completed_at)}</span></div>}
        {data.price && <div><span>{t('track.label_amount')}</span><span>{data.price}</span></div>}
      </div>
      {!compact && <Link className="more" to={`/suivi/${encodeURIComponent(data.ref)}`}>{t('track.see_full')} →</Link>}
    </div>
  );
}

export default function TrackRepair() {
  const { t } = useLanguage();
  const [ref, setRef] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const lookup = async (value) => {
    const v = normalizeRef(value);
    setError(''); setResult(null);
    if (!REF_RE.test(v)) { setError(t('track.err_invalid_ref')); return; }
    setLoading(true);
    try {
      setResult(await fetchSuivi(v));
    } catch (err) {
      setError(err?.response?.data?.detail || t('track.err_not_found'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('ref');
    if (p) { setRef(normalizeRef(p)); lookup(p); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card track" data-testid="repair-tracking-section">
      <h3><Search /> {t('track.title')}</h3>
      <form onSubmit={(e) => { e.preventDefault(); lookup(ref); }}>
        <label className="sr-only" htmlFor="rmv-ref">{t('track.label_ref')}</label>
        <input
          id="rmv-ref" className="input mono" value={ref} autoComplete="off"
          onChange={(e) => setRef(normalizeRef(e.target.value))}
          placeholder={t('track.input_placeholder')}
          data-testid="remobiles-track-input"
        />
        <button className="btn btn-accent" type="submit" disabled={loading} data-testid="remobiles-track-btn">{loading ? '…' : t('track.btn_track')}</button>
      </form>
      <button type="button" className="qr" onClick={() => setScanOpen(true)} data-testid="scan-public-cta"><ScanLine /> {t('track.scan_qr')}</button>
      {error && <div className="err" data-testid="remobiles-track-error">{error}</div>}
      <TrackResult data={result} />
      <QrScannerModal open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
}
